import React, { useState, useEffect } from 'react';

const AnalyticsDashboard = () => {
  const [summary, setSummary] = useState([]);
  const [selectedTest, setSelectedTest] = useState(null);
  const [testDetails, setTestDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('adminToken');

  useEffect(() => {
    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ab/analytics/summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setSummary(data.summary || []);
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTestDetails = async (testId) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ab/analytics/${testId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setTestDetails(data);
      setSelectedTest(testId);
    } catch (err) {
      console.error('Failed to fetch test details:', err);
    }
  };

  const getWinningVariant = (results) => {
    if (!results || results.length === 0) return null;
    return results.reduce((prev, current) => 
      (prev.conversion_rate > current.conversion_rate) ? prev : current
    );
  };

  if (loading) return <div className="text-center py-8">Loading analytics...</div>;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-800">A/B Testing Analytics</h2>
        <p className="text-gray-600 text-sm mt-1">Track performance and identify winning variants</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm">
          <div className="text-3xl font-bold text-teal-600">{summary.length}</div>
          <div className="text-gray-600">Total Tests</div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm">
          <div className="text-3xl font-bold text-blue-600">
            {summary.reduce((sum, t) => sum + t.total_views, 0)}
          </div>
          <div className="text-gray-600">Total Views</div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm">
          <div className="text-3xl font-bold text-green-600">
            {summary.reduce((sum, t) => sum + t.total_conversions, 0)}
          </div>
          <div className="text-gray-600">Total Conversions</div>
        </div>
      </div>

      {/* Tests Overview */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b bg-gray-50">
          <h3 className="font-semibold text-gray-800">All Tests Performance</h3>
        </div>
        
        {summary.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No A/B tests have been run yet. Create a test to start collecting data.
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4">Test Name</th>
                <th className="text-left py-3 px-4">Page</th>
                <th className="text-right py-3 px-4">Views</th>
                <th className="text-right py-3 px-4">Conversions</th>
                <th className="text-right py-3 px-4">Conv. Rate</th>
                <th className="text-center py-3 px-4">Status</th>
                <th className="text-right py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {summary.map((item) => (
                <tr 
                  key={item.test.id} 
                  className={`border-t hover:bg-gray-50 cursor-pointer ${selectedTest === item.test.id ? 'bg-teal-50' : ''}`}
                  onClick={() => fetchTestDetails(item.test.id)}
                >
                  <td className="py-3 px-4 font-medium">{item.test.name}</td>
                  <td className="py-3 px-4 text-gray-600">{item.test.page}</td>
                  <td className="py-3 px-4 text-right">{item.total_views}</td>
                  <td className="py-3 px-4 text-right">{item.total_conversions}</td>
                  <td className="py-3 px-4 text-right font-semibold text-green-600">
                    {item.conversion_rate}%
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${item.test.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                      {item.test.active ? 'Running' : 'Paused'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <button className="text-teal-600 hover:underline">
                      View Details →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Test Details Modal */}
      {testDetails && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">{testDetails.test.name}</h3>
              <p className="text-sm text-gray-500">Page: {testDetails.test.page} | Element: {testDetails.test.element}</p>
            </div>
            <button 
              onClick={() => { setSelectedTest(null); setTestDetails(null); }}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕ Close
            </button>
          </div>

          {/* Variant Comparison */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {testDetails.results.map((result, index) => {
              const isWinner = getWinningVariant(testDetails.results)?.variant.id === result.variant.id && result.views > 0;
              return (
                <div 
                  key={result.variant.id}
                  className={`p-4 rounded-lg border-2 ${isWinner ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}
                >
                  <div className="flex justify-between items-center mb-3">
                    <span className="font-semibold text-gray-800">{result.variant.name}</span>
                    {isWinner && <span className="bg-green-500 text-white text-xs px-2 py-1 rounded">WINNER</span>}
                  </div>
                  
                  <div className="text-sm text-gray-600 mb-3">
                    "{result.variant.content?.text || 'Default content'}"
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-blue-600">{result.views}</div>
                      <div className="text-xs text-gray-500">Views</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-purple-600">{result.clicks}</div>
                      <div className="text-xs text-gray-500">Clicks</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-600">{result.conversion_rate}%</div>
                      <div className="text-xs text-gray-500">Conv. Rate</div>
                    </div>
                  </div>

                  {/* Conversion breakdown */}
                  <div className="mt-3 pt-3 border-t text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Form Submits:</span>
                      <span className="font-medium">{result.form_submits}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Phone Clicks:</span>
                      <span className="font-medium">{result.phone_clicks}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Statistical Significance Note */}
          {testDetails.results.some(r => r.views > 0) && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-medium text-yellow-800">Statistical Note</h4>
              <p className="text-sm text-yellow-700 mt-1">
                For statistically significant results, aim for at least 100 conversions per variant. 
                Current sample sizes may not be sufficient for confident conclusions.
              </p>
            </div>
          )}

          {/* Recent Events */}
          {testDetails.recent_events && testDetails.recent_events.length > 0 && (
            <div className="mt-6">
              <h4 className="font-semibold text-gray-800 mb-3">Recent Events</h4>
              <div className="max-h-48 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left py-2 px-3">Event</th>
                      <th className="text-left py-2 px-3">Variant</th>
                      <th className="text-left py-2 px-3">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {testDetails.recent_events.map((event, i) => (
                      <tr key={i} className="border-t">
                        <td className="py-2 px-3">
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            event.event_type === 'view' ? 'bg-blue-100 text-blue-800' :
                            event.event_type === 'click' ? 'bg-purple-100 text-purple-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {event.event_type}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-gray-600">
                          {testDetails.results.find(r => r.variant.id === event.variant_id)?.variant.name || event.variant_id}
                        </td>
                        <td className="py-2 px-3 text-gray-500">
                          {new Date(event.timestamp).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
